import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { p as parseHrefProp } from './index10.js';
import { g as getDataAttributes, s as splitElementAttributes } from './data-attributes.js';
import { d as defineCustomElement$g } from './box.js';
import { d as defineCustomElement$f } from './check-input.js';
import { d as defineCustomElement$e } from './close-button.js';
import { d as defineCustomElement$d } from './divider.js';
import { d as defineCustomElement$c } from './form-field.js';
import { d as defineCustomElement$b } from './icon.js';
import { d as defineCustomElement$a } from './list.js';
import { d as defineCustomElement$9 } from './list-item.js';
import { d as defineCustomElement$8 } from './list-item-header.js';
import { d as defineCustomElement$7 } from './logo.js';
import { d as defineCustomElement$6 } from './scroll-indicator.js';
import { d as defineCustomElement$5 } from './status.js';
import { d as defineCustomElement$4 } from './tag.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const mainNavCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;background-color:var(--jw-theme-surface-navigation-bg-color);border-right:1px solid var(--jw-theme-surface-divider-color);box-sizing:border-box;display:flex;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-height:100%;padding:.5rem;transition:width .2s ease;width:15rem}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host wui-scroll-indicator::part(inner){gap:.75rem}nav{display:flex;flex:1;flex-direction:column;max-height:100%}.top-drawer{display:flex;flex:0;flex-direction:column;gap:.75rem;margin-bottom:1rem}.navigation-section{align-items:stretch;display:flex;flex:1;flex-direction:column;max-height:100%;overflow:hidden}.logo-section{align-items:center;align-self:stretch;display:flex;justify-content:space-between;padding:.5rem}.bottom-drawer{align-items:stretch;display:flex;flex:0;flex-direction:column;gap:.5rem}:host([collapsed]){width:3.5rem}:host([collapsed]) .logo-section{flex-direction:column;gap:.75rem;padding:.5rem 0}:host([collapsed]) wui-scroll-indicator::part(inner){gap:.5rem}";
const WuiMainNavStyle0 = mainNavCss;

const WuiMainNav$1 = /*@__PURE__*/ proxyCustomElement(class WuiMainNav extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.collapseChanged = createEvent(this, "collapseChanged", 7);
        this.logoLink = undefined;
        this.navigation = [];
        this.bottomDrawer = [];
        this.collapsed = undefined;
        this.collapsible = true;
    }
    toggleCollapse() {
        this.collapsed = !this.collapsed;
        this.collapseChanged.emit(this.collapsed);
    }
    renderLogo() {
        const logoElement = h("wui-logo", { name: "mark", type: "color", size: this.collapsed ? '2xs' : 'sm' });
        if (this.logoLink) {
            return h("a", { ...parseHrefProp(this.logoLink) }, logoElement);
        }
        return logoElement;
    }
    renderNavigationLinks() {
        if (this.navigation.length === 0) {
            return null;
        }
        return (h("section", { class: "navigation-section" }, h("wui-scroll-indicator", { showTagsLabel: this.collapsed ? false : true }, this.navigation.map((section, idx) => (
        // Cannot pass a key to a fragment
        // eslint-disable-next-line react/jsx-key
        h(Fragment, null, h("wui-list", { ...getDataAttributes(section) }, section.title && !this.collapsed && section.links.length > 0 && (h("wui-list-item-header", { key: `section-title-${idx}`, label: section.title })), section.links.map((link, idx) => {
            const { dataAttributes, elementAttributes } = splitElementAttributes(link);
            return (h("wui-list-item", { ...(typeof link !== 'string' ? dataAttributes : {}), key: `${link.label}-${idx}`, highlight: link.highlight, label: !this.collapsed ? link.label : undefined, as: ['a', parseHrefProp(elementAttributes)], icon: link.icon, variant: "nav" }));
        })), this.collapsed && idx !== this.navigation.length - 1 && h("wui-divider", null)))))));
    }
    renderBottomDrawer() {
        if (this.bottomDrawer.length === 0) {
            return null;
        }
        return (h("section", { class: "bottom-drawer" }, h("wui-divider", null), h("wui-list", null, this.bottomDrawer.map((drawerElement, idx) => {
            if (drawerElement.type === 'link') {
                const { dataAttributes, elementAttributes } = splitElementAttributes(drawerElement);
                return (h("wui-list-item", { ...(typeof drawerElement !== 'string' ? dataAttributes : {}), key: `${drawerElement.label}-${idx}`, label: !this.collapsed ? drawerElement.label : undefined, highlight: drawerElement.highlight, as: ['a', parseHrefProp(elementAttributes)], icon: drawerElement.icon, variant: "nav" }));
            }
            return (h("wui-list-item", { key: `${drawerElement.label}-${idx}`, ...drawerElement, label: !this.collapsed ? drawerElement.label : undefined, variant: "nav" }));
        }))));
    }
    render() {
        return (h(Host, { key: '6e2afb214d755a0bac96437f5d1bf02826f3e865' }, h("nav", { key: 'c59106ef666ea868db4d333b1d0bb75e935bbb72' }, h("div", { key: 'aaa115527da5d4f193ad4a1ecd56d9c5def21efc', class: "top-drawer" }, h("section", { key: '8c19c5de16823fbe4a0606e8e208176c99aa0438', class: "logo-section" }, this.renderLogo(), this.collapsible && (h("wui-close-button", { key: '2692ffb920c4de8c4031557bf1db16b5081bb3f1', icon: !this.collapsed ? 'layout-sidebar-left-collapse' : 'layout-sidebar-left-expand', tooltip: !this.collapsed ? 'Collapse' : 'Expand', onClick: () => this.toggleCollapse() }))), !this.collapsed && h("slot", { key: '602afddc5aeff4fe5b13e127e531f255dea47a01', name: "top-content" })), this.renderNavigationLinks(), this.renderBottomDrawer())));
    }
    static get style() { return WuiMainNavStyle0; }
}, [1, "wui-main-nav", {
        "logoLink": [16],
        "navigation": [16],
        "bottomDrawer": [16],
        "collapsed": [1540],
        "collapsible": [4]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-main-nav", "wui-box", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-list", "wui-list-item", "wui-list-item-header", "wui-logo", "wui-scroll-indicator", "wui-status", "wui-tag", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-main-nav":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiMainNav$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-logo":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-scroll-indicator":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-tag":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiMainNav = WuiMainNav$1;
const defineCustomElement = defineCustomElement$1;

export { WuiMainNav, defineCustomElement };

//# sourceMappingURL=wui-main-nav.js.map